import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquarePlus, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;
const TYPES = [
  { id: 'change', label: 'Change a section', hint: 'Fix or update something in an existing section' },
  { id: 'new_content', label: 'Add new content', hint: 'Propose a new section' },
  { id: 'removal', label: 'Remove a section', hint: 'Content that is outdated, wrong or not needed' },
];
const STATUS_TONE = { pending: 'text-amber-300', approved: 'text-emerald-300', rejected: 'text-rose-300' };
const field = 'w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-navigate-gold';

function MyRequests({ items }) {
  if (!items.length) return null;
  return (
    <div className="border-t border-zinc-800 pt-4" data-testid="my-change-requests">
      <p className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Your requests for this course</p>
      <div className="space-y-2 max-h-40 overflow-y-auto">
        {items.map((r) => (
          <div key={r.request_id} className="text-sm rounded-lg bg-zinc-900/60 p-2.5" data-testid={`my-change-request-${r.request_id}`}>
            <div className="flex justify-between gap-2">
              <span className="text-zinc-200 truncate min-w-0">{TYPES.find((t) => t.id === r.type)?.label}{r.section_title ? ` · ${r.section_title}` : ''}</span>
              <span className={`text-xs capitalize shrink-0 ${STATUS_TONE[r.status]}`}>{r.status === 'rejected' ? 'Declined' : r.status}</span>
            </div>
            {r.review_note && <p className="text-xs text-zinc-400 mt-1">Reviewer: {r.review_note}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RequestChangeDialog({ courseId, courseTitle, sections, currentIndex, token, compact = false }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('change');
  const [idx, setIdx] = useState('');
  const [description, setDescription] = useState('');
  const [suggest, setSuggest] = useState(false);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);

  const loadMine = () => axios.get(`${API}/change-requests/mine`, { headers })
    .then((r) => setMine((r.data.requests || []).filter((x) => x.course_id === courseId))).catch(() => {});

  useEffect(() => {
    if (!open) return;
    setIdx(currentIndex != null ? String(currentIndex) : '');
    loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (suggest && type === 'change' && idx !== '') setText(sections[Number(idx)]?.content || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggest, idx, type]);

  const needsSection = type !== 'new_content';
  const valid = description.trim().length >= 3 && (!needsSection || idx !== '') && (!suggest || type === 'removal' || text.trim()) && (!(suggest && type === 'new_content') || title.trim());

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/courses/${courseId}/change-requests`, {
        type, section_index: idx === '' ? null : Number(idx), description,
        suggested_title: suggest && type === 'new_content' ? title : null,
        suggested_text: suggest && type !== 'removal' ? text : null,
      }, { headers });
      toast.success('Request sent — a super admin will review it');
      setDescription(''); setSuggest(false); setTitle(''); setText('');
      loadMine();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send request');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="request-change-btn"
        className={compact ? 'text-xs px-3 py-1.5 rounded-full border border-zinc-700 text-zinc-300 hover:border-navigate-gold hover:text-white transition-colors'
          : 'w-full flex items-center gap-3 p-3 rounded-lg text-left border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors'}>
        <MessageSquarePlus className="h-4 w-4 shrink-0" /><span className="truncate">Request a change</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-navigate-surface border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="request-change-dialog">
          <DialogHeader><DialogTitle>Request a change{courseTitle ? ` — ${courseTitle}` : ''}</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-400 -mt-2">Only super admins can edit courses. Your request goes to them for approval.</p>
          <div className="grid sm:grid-cols-3 gap-2">
            {TYPES.map((t) => (
              <button key={t.id} type="button" onClick={() => setType(t.id)} data-testid={`request-type-${t.id}`}
                className={`text-left rounded-lg border p-3 transition-colors ${type === t.id ? 'border-navigate-gold bg-navigate-gold/10' : 'border-zinc-700 hover:border-zinc-500'}`}>
                <p className="text-sm font-medium">{t.label}</p><p className="text-[11px] text-zinc-400 mt-0.5">{t.hint}</p>
              </button>
            ))}
          </div>
          <label className="block text-xs text-zinc-400">{needsSection ? 'Section' : 'Add after section (optional)'}
            <select value={idx} onChange={(e) => setIdx(e.target.value)} className={`${field} mt-1`} data-testid="request-section-select">
              <option value="">{needsSection ? 'Choose a section…' : 'At the end of the course'}</option>
              {sections.map((s, i) => <option key={s.section_id || i} value={i}>{i + 1}. {s.title}</option>)}
            </select>
          </label>
          <label className="block text-xs text-zinc-400">What should change and why?
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className={`${field} mt-1`} data-testid="request-description-input"
              placeholder={type === 'removal' ? 'Why should this section be removed?' : type === 'new_content' ? 'What content is missing?' : 'What is wrong or out of date?'} />
          </label>
          {type !== 'removal' && (
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={suggest} onChange={(e) => setSuggest(e.target.checked)} data-testid="request-suggest-toggle" />
              {type === 'change' ? 'Suggest the exact new text for this section' : 'Write the new section'}
            </label>
          )}
          {suggest && type === 'new_content' && <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New section title" className={field} data-testid="request-suggested-title" />}
          {suggest && type !== 'removal' && (
            <textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} className={`${field} font-mono text-xs`} data-testid="request-suggested-text"
              placeholder="Write the content (Markdown is fine)" />
          )}
          <div className="flex justify-end">
            <button type="button" onClick={submit} disabled={!valid || busy} className="btn-gold px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="request-submit-btn">
              <Send className="h-4 w-4" />{busy ? 'Sending…' : 'Send request'}
            </button>
          </div>
          <MyRequests items={mine} />
        </DialogContent>
      </Dialog>
    </>
  );
}
